import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Package, ListChecks, Clock, Truck, CheckCircle, Warning, Plus, ArrowRight, Lightning, MapPin, Star, Buildings, MagnifyingGlass } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  received: { label: "Received", color: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: Clock },
  quoted: { label: "Quoted", color: "bg-cyan-50 text-cyan-700 border-cyan-200", icon: Clock },
  approved: { label: "Approved", color: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle },
  info_needed: { label: "Info Needed", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Warning },
  compounding: { label: "Compounding", color: "bg-blue-50 text-blue-700 border-blue-200", icon: Package },
  shipped: { label: "Shipped", color: "bg-violet-50 text-violet-700 border-violet-200", icon: Truck },
  delivered: { label: "Delivered", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle },
};

export default function DashboardPage() {
  const { user, checkAuth } = useAuth();
  const isAdmin = user?.role === "master_admin";
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);

  // Join another clinic
  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [foundOrg, setFoundOrg] = useState(null);
  const [searching, setSearching] = useState(false);
  const [joining, setJoining] = useState(false);

  const searchOrg = async () => {
    if (!joinCode || joinCode.length < 4) { toast.error("Enter the org code from your clinic admin"); return; }
    setSearching(true);
    setFoundOrg(null);
    try {
      const { data } = await axios.get(`${API}/orgs/search?code=${encodeURIComponent(joinCode)}`);
      if (data.found) setFoundOrg(data.org);
      else toast.error("No active clinic found with that code");
    } catch { toast.error("Search failed"); }
    setSearching(false);
  };

  const submitJoin = async () => {
    if (!foundOrg) return;
    setJoining(true);
    try {
      await axios.post(`${API}/me/orgs/join`, { org_code: foundOrg.code }, { withCredentials: true });
      toast.success(`Request sent to ${foundOrg.name}. Their admin will review.`);
      setShowJoin(false); setJoinCode(""); setFoundOrg(null);
      await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Join failed");
    }
    setJoining(false);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        if (isAdmin) {
          const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
          setStats(data);
        }
        const { data: ordersData } = await axios.get(`${API}/orders?limit=5`, { withCredentials: true });
        setRecentOrders(ordersData.orders);
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [isAdmin]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
            {isAdmin ? "ADMIN DASHBOARD" : user?.role?.replace("_", " ").toUpperCase() + " DASHBOARD"}
          </p>
          <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tighter leading-none text-zinc-950 mt-2">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
        </div>
        {!isAdmin && (
          <button
            onClick={() => { setJoinCode(""); setFoundOrg(null); setShowJoin(true); }}
            className="px-4 py-2.5 text-sm font-semibold border border-zinc-300 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors flex items-center gap-2"
            data-testid="join-another-clinic-btn"
          >
            <Buildings size={16} weight="bold" /> Join Another Clinic
          </button>
        )}
      </div>

      {/* Admin Stats */}
      {isAdmin && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border border-zinc-200 mb-8" data-testid="admin-stats-grid">
          {[
            { label: "Total Orders", value: stats.total_orders, href: "/admin/orders" },
            { label: "Pending", value: stats.pending_orders, href: "/admin/orders" },
            { label: "Compounding", value: stats.compounding, href: "/admin/orders" },
            { label: "Shipped", value: stats.shipped, href: "/admin/orders" },
          ].map((s, i) => (
            <Link
              to={s.href}
              key={i}
              className="p-6 border-r border-b border-zinc-200 last:border-r-0 hover:bg-zinc-50 transition-colors"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">{s.label}</p>
              <p className="font-[Chivo] text-3xl font-black text-zinc-950 mt-2">{s.value}</p>
            </Link>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      {!isAdmin && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Link
            to="/orders/quote"
            className="border border-zinc-200 p-6 hover:-translate-y-1 hover:border-[#0055FF] transition-all duration-200 flex items-center gap-4"
            data-testid="quick-quote"
          >
            <div className="w-12 h-12 bg-amber-500 flex items-center justify-center flex-shrink-0">
              <Lightning size={24} weight="fill" className="text-white" />
            </div>
            <div>
              <h3 className="font-[Chivo] font-bold text-zinc-950">Quick Quote</h3>
              <p className="text-sm text-zinc-500 mt-0.5">Upload a script and get a quote</p>
            </div>
          </Link>
          <Link
            to="/orders/new"
            className="border border-zinc-200 p-6 hover:-translate-y-1 hover:border-[#0055FF] transition-all duration-200 flex items-center gap-4"
            data-testid="quick-new-order"
          >
            <div className="w-12 h-12 bg-[#0055FF] flex items-center justify-center flex-shrink-0">
              <Plus size={24} weight="bold" className="text-white" />
            </div>
            <div>
              <h3 className="font-[Chivo] font-bold text-zinc-950">Place Order</h3>
              <p className="text-sm text-zinc-500 mt-0.5">Browse catalog and order</p>
            </div>
          </Link>
          <Link
            to="/orders/tracking"
            className="border border-zinc-200 p-6 hover:-translate-y-1 hover:border-[#0055FF] transition-all duration-200 flex items-center gap-4"
            data-testid="quick-tracking"
          >
            <div className="w-12 h-12 bg-violet-500 flex items-center justify-center flex-shrink-0">
              <MapPin size={24} weight="fill" className="text-white" />
            </div>
            <div>
              <h3 className="font-[Chivo] font-bold text-zinc-950">Track Orders</h3>
              <p className="text-sm text-zinc-500 mt-0.5">Live status of active orders</p>
            </div>
          </Link>
        </div>
      )}

      {/* Recent Orders */}
      <div className="border border-zinc-200" data-testid="recent-orders-section">
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <h2 className="font-[Chivo] font-bold text-zinc-950">Recent Orders</h2>
          <Link
            to={isAdmin ? "/admin/orders" : "/orders"}
            className="text-sm text-[#0055FF] hover:text-[#0044CC] font-medium flex items-center gap-1"
            data-testid="view-all-orders-link"
          >
            View All <ArrowRight size={14} />
          </Link>
        </div>
        {recentOrders.length === 0 ? (
          <div className="p-12 text-center">
            <ListChecks size={32} className="text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No orders yet</p>
            {!isAdmin && (
              <Link to="/orders/new" className="text-[#0055FF] text-sm font-medium mt-2 inline-block" data-testid="empty-new-order-link">
                Place your first order
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {recentOrders.map(order => {
              const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.received;
              const StatusIcon = sc.icon;
              return (
                <Link
                  key={order.id}
                  to={isAdmin ? `/admin/orders` : `/orders/${order.id}`}
                  className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors"
                  data-testid={`order-row-${order.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-400">#{order.id?.slice(0, 8)}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono font-medium border ${sc.color}`}>
                        <StatusIcon size={12} />
                        {sc.label}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-800 mt-1 truncate">
                      {isAdmin ? `${order.user_name} - ` : ""}{order.patient_name} ({order.items?.length} item{order.items?.length !== 1 ? "s" : ""})
                    </p>
                  </div>
                  <div className="text-right ml-4 flex-shrink-0">
                    <p className="text-sm font-semibold text-zinc-950">${order.total?.toFixed(2)}</p>
                    <p className="text-xs text-zinc-400">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Info banner */}
      <div className="mt-8 border border-zinc-200 p-6 bg-zinc-50" data-testid="info-banner">
        <div className="flex flex-wrap gap-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">TURNAROUND TIME</p>
            <p className="text-sm font-semibold text-zinc-950 mt-1">2-3 business days</p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ORDER CUT-OFF</p>
            <p className="text-sm font-semibold text-zinc-950 mt-1">12:00 PM daily</p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">CONTACT</p>
            <p className="text-sm font-semibold text-zinc-950 mt-1">07-3356-3964</p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">EMAIL</p>
            <p className="text-sm font-semibold text-zinc-950 mt-1">newmarket@amcal.net.au</p>
          </div>
        </div>
      </div>

      {/* Join Another Clinic Dialog */}
      <Dialog open={showJoin} onOpenChange={setShowJoin}>
        <DialogContent className="sm:max-w-md" data-testid="join-clinic-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Join Another Clinic</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">Already a member of a clinic? You can request to join another using its org code. Each clinic's admin will review your request separately.</p>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Org Code</Label>
              <div className="flex gap-2 mt-1.5">
                <Input value={joinCode} onChange={e => { setJoinCode(e.target.value.toUpperCase()); setFoundOrg(null); }} placeholder="e.g. 1024-5567" className="rounded-none flex-1 font-mono" data-testid="join-org-code-input" />
                <button onClick={searchOrg} disabled={searching} className="px-4 py-2 text-sm font-semibold bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 disabled:opacity-50 flex items-center gap-1" data-testid="search-clinic-btn">
                  <MagnifyingGlass size={14} /> {searching ? "..." : "Search"}
                </button>
              </div>
            </div>
            {foundOrg && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 flex items-start gap-2" data-testid="join-found-org">
                <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-zinc-950">{foundOrg.name}</p>
                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-0.5">{foundOrg.type?.replace("_", " ")} · {foundOrg.code}</p>
                </div>
              </div>
            )}
            {foundOrg && (
              <button onClick={submitJoin} disabled={joining} className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC] disabled:opacity-50" data-testid="confirm-join-btn">
                {joining ? "SENDING..." : `REQUEST TO JOIN ${foundOrg.name.toUpperCase()}`}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
